// The create/edit booking form: its limits, its validation and the
// payloads it sends. No rendering.
//
// Staff are exempt from the booking rules customers are held to (Opening
// Hours, no past dates, party size 1-50): the manager API accepts any
// readable date and time and any party size of 1 or more, so that is what
// this form accepts too. The limits below are checked against
// `CreateBookingRequest` / `ManagerUpdateBookingRequest` in
// ai-receptionist-bot `app/schemas/bookings.py`.

import { isGuestVenueVertical } from '../../utils/verticals'
import { isValidPhoneNumber, normalizePhoneNumber } from '../../utils/phone'

export const BOOKING_FORM_LIMITS = {
  // Backend: party_size >= 1, no upper bound for staff.
  guestsMin: 1,
  // Backend: duration_minutes >= 1.
  durationMin: 1,
  // The form's own: the backend sets no length on a name.
  nameMaxLength: 100,
}

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/

export function getEmptyBookingForm(vertical = 'fnb') {
  return {
    name: '',
    phone: '',
    email: '',
    date: '',
    time: vertical === 'fnb' ? '19:00' : '09:00',
    guests: isGuestVenueVertical(vertical) ? 2 : 1,
    table: '',
    service: '',
    durationMinutes: '',
    staff: '',
    notes: '',
    contactTags: [],
  }
}

/** The form filled from an existing booking, for the edit dialog. */
export function getBookingEditForm(booking, vertical = 'fnb') {
  return {
    ...getEmptyBookingForm(vertical),
    name: booking?.customer_name || '',
    phone: normalizePhoneNumber(booking?.customer_phone),
    date: booking?.date || '',
    time: String(booking?.time_slot || '').slice(0, 5) || getEmptyBookingForm(vertical).time,
    guests: booking?.guests || (isGuestVenueVertical(vertical) ? 2 : 1),
    service: booking?.service_ref || '',
    durationMinutes: booking?.duration_minutes != null ? String(booking.duration_minutes) : '',
    staff: booking?.staff_label || '',
    notes: booking?.notes || '',
  }
}

export function isValidIsoDate(dateStr) {
  if (!DATE_RE.test(dateStr)) return false
  const [year, month, day] = dateStr.split('-').map(Number)
  const utcDate = new Date(Date.UTC(year, month - 1, day))
  return (
    utcDate.getUTCFullYear() === year &&
    utcDate.getUTCMonth() === month - 1 &&
    utcDate.getUTCDate() === day
  )
}

function dedupeNonEmptyStrings(values) {
  const seen = new Set()
  const out = []
  values.forEach((value) => {
    const str = String(value || '').trim()
    if (!str || seen.has(str)) return
    seen.add(str)
    out.push(str)
  })
  return out
}

/**
 * Validate a booking form.
 *
 * `validateCustomer` is off for the edit dialog, which cannot change who
 * the booking is for. `requireGuests` is on for verticals where party size
 * matters. Returns `{ errors, normalized }`; `errors` maps a field to its
 * translated message and is empty when the form is valid.
 */
export function validateBookingForm(form, fallbackDate, t, options = {}) {
  const { validateCustomer = true, requireGuests = true } = options
  const errors = {}
  const normalizedName = (form.name || '').trim()
  const normalizedPhone = normalizePhoneNumber(form.phone)
  const normalizedEmail = (form.email || '').trim().toLowerCase()
  const dateStr = (form.date || fallbackDate || '').trim()
  const timeStr = (form.time || '19:00').trim()
  const guestsNum = Number(form.guests)
  const contactTagIds = dedupeNonEmptyStrings(
    (form.contactTags || []).map((tag) => tag?.id ?? tag?.value),
  )

  if (validateCustomer) {
    if (!normalizedName) {
      errors.name = t('bookings.errorNameRequired')
    } else if (normalizedName.length > BOOKING_FORM_LIMITS.nameMaxLength) {
      errors.name = t('bookings.errorNameTooLong', { max: BOOKING_FORM_LIMITS.nameMaxLength })
    }

    if (!normalizedPhone) {
      errors.phone = t('bookings.errorPhoneRequired')
    } else if (!isValidPhoneNumber(normalizedPhone)) {
      errors.phone = t('bookings.errorPhoneInvalid')
    }

    if (contactTagIds.length > 0 && !normalizedPhone) {
      errors.phone = t('bookings.errorContactTagsRequirePhone')
    }
  }

  if (normalizedEmail && !EMAIL_RE.test(normalizedEmail)) {
    errors.email = t('bookings.errorEmailInvalid')
  }

  if (!dateStr || !isValidIsoDate(dateStr)) {
    errors.date = t('bookings.errorDateInvalid')
  }

  if (!TIME_RE.test(timeStr)) {
    errors.time = t('bookings.errorTimeInvalid')
  }

  const guestsProvided = String(form.guests ?? '').trim() !== ''
  const guestsValid = Number.isInteger(guestsNum) && guestsNum >= BOOKING_FORM_LIMITS.guestsMin
  if ((requireGuests || guestsProvided) && !guestsValid) {
    errors.guests = t('bookings.errorGuestsMin', { min: BOOKING_FORM_LIMITS.guestsMin })
  }

  const durationRaw = String(form.durationMinutes ?? '').trim()
  const durationNum = durationRaw ? Number(durationRaw) : null
  if (durationRaw && (!Number.isInteger(durationNum) || durationNum < BOOKING_FORM_LIMITS.durationMin)) {
    errors.durationMinutes = t('bookings.errorDurationInvalid')
  }

  return {
    errors,
    normalized: {
      name: normalizedName,
      phone: normalizedPhone,
      email: normalizedEmail,
      date: dateStr,
      time: timeStr,
      guests: guestsValid ? guestsNum : 1,
      service: (form.service || '').trim(),
      durationMinutes: durationNum,
      staff: (form.staff || '').trim(),
      notes: form.notes?.trim() || '',
      contactTagIds,
    },
  }
}

function optionalFields(normalized) {
  return {
    ...(normalized.service ? { service_ref: normalized.service } : {}),
    ...(normalized.durationMinutes ? { duration_minutes: normalized.durationMinutes } : {}),
    ...(normalized.staff ? { staff_label: normalized.staff } : {}),
  }
}

/** The body of `POST /manager/bookings/{tenant_id}`. */
export function buildBookingPayload(normalized) {
  return {
    customer_name: normalized.name,
    customer_phone: normalized.phone,
    date: normalized.date,
    time_slot: normalized.time,
    party_size: normalized.guests,
    notes: normalized.notes,
    ...optionalFields(normalized),
    ...(normalized.contactTagIds?.length ? { contact_tag_ids: normalized.contactTagIds } : {}),
  }
}

/**
 * The body of `PATCH /manager/bookings/{tenant_id}/{booking_id}`. Only
 * booking-owned fields: the endpoint rejects customer identity and tags.
 */
export function buildBookingUpdatePayload(normalized) {
  return {
    date: normalized.date,
    time_slot: normalized.time,
    party_size: normalized.guests,
    notes: normalized.notes,
    ...optionalFields(normalized),
  }
}
